import { createFileRoute, Link } from "@tanstack/react-router";
import { gatedRobotsMeta } from "@/lib/seo-gating";
import { ArrowLeft, BookOpen, CheckCircle2 } from "lucide-react";
import { DBT_INTRO, DBT_MODULES, totalSkillCount } from "@/data/learn/dbt";
import { useDbtProgress } from "@/hooks/useDbtProgress";

export const Route = createFileRoute("/learn/dbt/")({
  head: () => ({
    meta: [
      { title: "DBT Skills — Recovery Trauma™" },
      { name: "description", content: "Interactive Dialectical Behaviour Therapy skills — mindfulness, distress tolerance, emotion regulation, interpersonal effectiveness, and walking the middle path. Overview, examples, practice, reflection and summary for every skill." },
      { property: "og:title", content: "DBT Skills — Recovery Trauma™" },
      { property: "og:description", content: "Learn DBT skills interactively: 5-minute reads, worked examples, practice space, reflection prompts and summaries." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://recoverytrauma.com/learn/dbt" },
      { name: "twitter:card", content: "summary_large_image" },
      ...gatedRobotsMeta("/learn/dbt"),
    ],
    links: [{ rel: "canonical", href: "https://recoverytrauma.com/learn/dbt" }],
  }),
  component: DbtHub,
});

const THEME_STYLES: Record<string, { card: string; chip: string; ring: string }> = {
  sage: { card: "bg-gradient-to-br from-emerald-50 to-emerald-100 dark:from-emerald-950/30 dark:to-emerald-900/20", chip: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-200", ring: "stroke-emerald-500" },
  sky:  { card: "bg-gradient-to-br from-sky-50 to-sky-100 dark:from-sky-950/30 dark:to-sky-900/20",             chip: "bg-sky-100 text-sky-800 dark:bg-sky-900/50 dark:text-sky-200",           ring: "stroke-sky-500" },
  peach:{ card: "bg-gradient-to-br from-orange-50 to-rose-100 dark:from-rose-950/30 dark:to-orange-900/20",     chip: "bg-rose-100 text-rose-800 dark:bg-rose-900/50 dark:text-rose-200",       ring: "stroke-rose-500" },
  lavender: { card: "bg-gradient-to-br from-violet-50 to-violet-100 dark:from-violet-950/30 dark:to-violet-900/20", chip: "bg-violet-100 text-violet-800 dark:bg-violet-900/50 dark:text-violet-200", ring: "stroke-violet-500" },
  gold: { card: "bg-gradient-to-br from-amber-50 to-yellow-100 dark:from-amber-950/30 dark:to-yellow-900/20",   chip: "bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-100",   ring: "stroke-amber-500" },
};

function ProgressRing({ done, total, themeKey }: { done: number; total: number; themeKey: string }) {
  const size = 56;
  const stroke = 5;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const pct = total ? done / total : 0;
  const offset = c * (1 - pct);
  const style = THEME_STYLES[themeKey] ?? THEME_STYLES.sage;
  return (
    <div className="relative inline-flex items-center justify-center">
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} strokeWidth={stroke} className="fill-none stroke-muted-foreground/20" />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          strokeWidth={stroke}
          strokeDasharray={c}
          strokeDashoffset={offset}
          strokeLinecap="round"
          className={`fill-none ${style.ring} transition-all`}
        />
      </svg>
      <span className="absolute text-xs font-semibold text-foreground">{done}/{total}</span>
    </div>
  );
}

function DbtHub() {
  const { moduleProgress, map } = useDbtProgress();
  const totalDone = Object.keys(map).length;
  const total = totalSkillCount();

  return (
    <main className="mx-auto max-w-6xl px-4 py-10">
      <Link to="/learn" className="mb-6 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> Back to Learn
      </Link>

      <header className="mb-10 text-center">
        <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1 text-xs text-muted-foreground">
          <BookOpen className="h-3.5 w-3.5" /> DBT Toolkit
        </div>
        <h1 className="font-serif text-4xl tracking-tight text-foreground md:text-5xl">DBT Skills</h1>
        <p className="mx-auto mt-4 max-w-2xl text-base leading-relaxed text-muted-foreground">
          Learn practical skills to navigate difficult emotions, improve relationships, and build a life worth living.
        </p>
        <p className="mx-auto mt-3 max-w-2xl text-sm leading-relaxed text-foreground/70">
          {DBT_INTRO}
        </p>
        <div className="mt-6 inline-flex items-center gap-2 rounded-full bg-emerald-50 px-4 py-1.5 text-xs font-semibold text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200">
          <CheckCircle2 className="h-3.5 w-3.5" />
          {totalDone} of {total} skills completed
        </div>
      </header>

      <ul className="grid gap-5 md:grid-cols-2">
        {DBT_MODULES.map((m) => {
          const style = THEME_STYLES[m.theme];
          const { done, total: t } = moduleProgress(m.id, m.skills.map((s) => s.id));
          return (
            <li key={m.id}>
              <Link
                to="/learn/dbt/$module"
                params={{ module: m.id }}
                className={`group flex h-full flex-col justify-between rounded-3xl border border-border/60 p-6 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md ${style.card}`}
              >
                <div>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-3xl">{m.emoji}</div>
                      <h2 className="mt-3 font-serif text-2xl text-foreground">{m.label}</h2>
                      <p className="mt-1 text-sm font-medium text-foreground/80">{m.tagline}</p>
                    </div>
                    <ProgressRing done={done} total={t} themeKey={m.theme} />
                  </div>
                  <p className="mt-4 text-sm leading-relaxed text-foreground/70">{m.intro}</p>
                </div>
                <div className="mt-5 flex flex-wrap gap-1.5">
                  {m.skills.map((s) => (
                    <span key={s.id} className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${style.chip}`}>
                      {s.emoji} {s.title}
                    </span>
                  ))}
                </div>
                <div className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-foreground/80 group-hover:underline">
                  Open module →
                </div>
              </Link>
            </li>
          );
        })}
      </ul>

      <p className="mt-10 rounded-2xl border border-border bg-muted/30 p-5 text-center text-xs leading-relaxed text-muted-foreground">
        Educational only. For urgent support see{" "}
        <Link to="/crisis" className="text-gold underline-offset-2 hover:underline">
          Crisis Resources
        </Link>
        .
      </p>
    </main>
  );
}
